// Utility functions and mock data for Deposit module
import BigNumber from 'bignumber.js'

// Helper functions for calculations
export const calculateBalance = (
  balance: string,
  amount: string,
  decimals: number
): string => {
  const result = Number(balance) - Number(amount)
  return result.toFixed(decimals)
}

export const calculateEquity = (
  equity: number,
  depositAmount: string,
  rate: string
): string => {
  const result = equity + Number(depositAmount) * Number(rate)
  return result.toFixed(2)
}

export const isGreaterThan = (
  a: string | number,
  b: string | number
): boolean => {
  return Number(a) > Number(b)
}

export const isLessThan = (a: string | number, b: string | number): boolean => {
  return Number(a) < Number(b)
}

/**
 * 格式化金额显示
 * 规则：
 * - 最长显示6位小数
 * - 如果末尾为0，则最多显示2位小数
 * - 保持至少2位小数（当整数部分为0时）
 *
 * @param value - 要格式化的数值（字符串或数字）
 * @returns 格式化后的字符串
 *
 * @example
 * formatAmount('0.000287645') // '0.000287'
 * formatAmount('0.00') // '0.00'
 * formatAmount('0.002800') // '0.0028'
 * formatAmount('15.21532') // '15.21532'
 * formatAmount('0.1520') // '0.152'
 * formatAmount('0.1') // '0.10'
 * formatAmount('0.11') // '0.11'
 * formatAmount('0.111') // '0.111'
 */
export const formatAmount = (value: string | number): string => {
  if (!value || value === '' || isNaN(Number(value))) {
    return '0.00'
  }

  const num = Number(value)

  // 如果是0，直接返回 '0.00'
  if (num === 0) {
    return '0.00'
  }

  // 转换为字符串进行处理，避免 toFixed 的四舍五入
  let str = num.toString()

  // 如果是科学计数法，先转换为普通数字
  if (str.includes('e')) {
    str = num.toFixed(20) // 使用足够大的精度
  }

  // 分离整数和小数部分
  const parts = str.split('.')
  const integerPart = parts[0]
  let decimalPart = parts[1] || ''

  // 截断到最多6位小数（不四舍五入）
  if (decimalPart.length > 6) {
    decimalPart = decimalPart.substring(0, 6)
  }

  // 移除末尾的0
  decimalPart = decimalPart.replace(/0+$/, '')

  // 如果没有小数部分或小数部分为空，添加 .00
  if (!decimalPart) {
    return integerPart + '.00'
  }

  // 如果小数部分少于2位，补齐到2位
  if (decimalPart.length === 1) {
    decimalPart += '0'
  }

  return integerPart + '.' + decimalPart
}

/**
 * 验证和格式化输入金额
 * @param input - 用户输入的字符串
 * @param maxBalance - 最大可用余额
 * @returns { isValid: boolean, formattedValue: string, errorType?: string }
 */
export const validateAndFormatInput = (
  input: string,
  maxBalance: string = '0'
): {
  isValid: boolean
  formattedValue: string
  errorType?:
    | 'invalid_char'
    | 'exceed_decimals'
    | 'exceed_balance'
    | 'auto_adjusted_to_max'
} => {
  // 规则1-3: 检查特殊符号、汉字、英文
  const invalidCharRegex = /[^0-9.]/
  if (invalidCharRegex.test(input)) {
    return {
      isValid: false,
      formattedValue: '',
      errorType: 'invalid_char',
    }
  }

  // 规则4: 如果只输入"."，转换为"0."
  if (input === '.') {
    return {
      isValid: true,
      formattedValue: '0.',
    }
  }

  // 检查是否有多个小数点
  const dotCount = (input.match(/\./g) || []).length
  if (dotCount > 1) {
    return {
      isValid: false,
      formattedValue: '',
      errorType: 'invalid_char',
    }
  }

  // 如果输入为空，返回空字符串
  if (!input || input === '') {
    return {
      isValid: true,
      formattedValue: '',
    }
  }

  // 规则5: 检查小数位数不超过6位
  const parts = input.split('.')
  if (parts.length === 2 && parts[1].length > 6) {
    // 截断到6位小数
    const truncated = parts[0] + '.' + parts[1].substring(0, 6)
    return validateAndFormatInput(truncated, maxBalance)
  }

  // 转换为数字进行余额检查
  const numValue = parseFloat(input)
  const maxBalanceNum = parseFloat(maxBalance)

  // 规则7,9: 检查是否超过余额 - 优化用户体验
  if (!isNaN(numValue) && !isNaN(maxBalanceNum) && numValue > maxBalanceNum) {
    // 如果用户输入超过余额，自动调整为最大余额
    // 这样提供更好的用户体验，而不是简单地拒绝输入
    const maxBalanceFormatted = formatAmount(maxBalance)
    return {
      isValid: true,
      formattedValue: maxBalanceFormatted,
      errorType: 'auto_adjusted_to_max',
    }
  }

  // 规则6: 保持用户输入的原始格式，不自动移除末尾0
  // 这样用户可以正常输入 12.0 然后继续输入 12.05
  const formattedValue = input

  return {
    isValid: true,
    formattedValue,
  }
}

/**
 * 格式化输入显示（用于实时显示）
 * 与 formatAmount 不同，这个函数用于输入过程中的实时格式化
 * 不强制添加小数位，保持用户输入的原始意图
 */
export const formatInputDisplay = (value: string): string => {
  if (!value || value === '' || value === '.') {
    return value
  }

  const num = parseFloat(value)
  if (isNaN(num)) {
    return value
  }

  // 如果原始输入以小数点结尾，保持小数点
  if (value.endsWith('.')) {
    return value
  }

  // 如果输入包含小数点，移除末尾的0
  if (value.includes('.')) {
    let str = value.replace(/0+$/, '')
    // 如果移除0后以小数点结尾，也移除小数点
    if (str.endsWith('.')) {
      str = str.slice(0, -1)
    }
    return str
  }

  // 如果是整数，直接返回整数形式
  return num.toString()
}

// Mock data and hooks - these would be replaced with actual implementations
export const mockUseTranslation = () => (key: string, params?: any) => {
  const translations: Record<string, string> = {
    Deposit: 'Deposit',
    From: 'From',
    Asset: 'Asset',
    Amount: 'Amount',
    Available: 'Available',
    MAX: 'MAX',
    'Deposit.details': 'Deposit Details',
    'Deposit.address': 'Deposit Address',
    copySuccess: 'Copied successfully',
    important: 'Important',
    depositAllTip: 'Please ensure you are depositing {coin} on {v} network',
    'Deposit.Arrivaltime': 'Arrival Time',
    'Deposit.minDeposit': 'Min Deposit',
    'Deposit.block.txConfirm': 'Block Confirmations',
    'Deposit.Arrival.Estimated': '~{v} minutes',
    enableSystemProWithToken: 'Enable {token} for {network}',
    enableTokenDesc: 'You need to enable {token} to deposit on {network}',
    enableWithToken: 'Enable {token}',
    confirmDeposit: 'Confirm Deposit',
    switchNetwork: 'Switch Network',
    minDeposit: 'Min deposit: {amount} {token}',
    Equity: 'Equity',
    availableDesc: 'Available balance for trading',
  }

  let result = translations[key] || key
  if (params) {
    Object.keys(params).forEach(param => {
      result = result.replace(new RegExp(`{${param}}`, 'g'), params[param])
    })
  }
  return result
}

export const mockUseToast = () => ({
  show: (_message: string, _options?: any) => {
    // Toast message would be shown here
  },
})

// Chain constants - 避免魔法数字
export const CHAIN_IDS = {
  ETHEREUM: 1,
  BSC: 56,
  POLYGON: 137,
  ARBITRUM: 42161,
  ARBITRUM_SEPOLIA: 421614, // Arbitrum Sepolia 测试网
  OPTIMISM: 10,
  AVALANCHE: 43114,
  SEPOLIA: 11155111,
} as const

// 不需要显示大额充值警告的链（ETH、Sepolia 和 Arbitrum 主网/测试网）
export const CHAINS_WITHOUT_LARGE_DEPOSIT_WARNING = [
  CHAIN_IDS.ETHEREUM,
  CHAIN_IDS.SEPOLIA,
  CHAIN_IDS.ARBITRUM,
  CHAIN_IDS.ARBITRUM_SEPOLIA,
] as const

// 工具函数：检查链是否需要显示大额充值警告
export const shouldShowLargeDepositWarning = (chain: {
  chainId?: string | number
}) => {
  // 基于 chainId 判断
  if (chain.chainId !== undefined) {
    const normalizedChainId =
      typeof chain.chainId === 'string'
        ? parseInt(chain.chainId, 10)
        : chain.chainId
    return !(
      CHAINS_WITHOUT_LARGE_DEPOSIT_WARNING as readonly number[]
    ).includes(normalizedChainId)
  }

  // 默认显示警告
  return true
}

/**
 * 检查是否应该显示 LayerZero 提示
 * 条件：Arbitrum 链 + USDT + 金额 >= 200,000
 */
export const shouldShowLayerZeroTip = (
  chainId: number | string | undefined,
  tokenSymbol: string | undefined,
  depositAmount: string | undefined
): boolean => {
  // 检查是否为 Arbitrum 链
  const numericChainId =
    typeof chainId === 'string' ? parseInt(chainId, 10) : chainId
  const isArbitrumChain = numericChainId === 42161 || numericChainId === 421614

  // 检查是否为 USDT
  const isUSDT = tokenSymbol?.toUpperCase() === 'USDT'

  if (!isArbitrumChain || !isUSDT) {
    return false
  }

  // 检查金额是否 >= 200,000
  try {
    const amountBN = new BigNumber(depositAmount || '0')
    const thresholdBN = new BigNumber('200000')

    if (amountBN.isNaN() || thresholdBN.isNaN()) {
      return false
    }

    return amountBN.gte(thresholdBN)
  } catch (error) {
    return false
  }
}
